import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * The create-appointment call chain.
 *
 * Every endpoint and body here was verified live against dev1 on 2026-07-15 as
 * a real logged-in doctor — see scratchpad/create-appointment-VERIFIED.md.
 * Do NOT re-derive any of this from the local api-appointment-service checkout:
 * it is on sticky-floater-release, 282 commits behind upstream/development, and
 * the two disagree on the create path's error handling.
 *
 * Scope: CONSULTATION (Tele / Video / In-person). Booking for the patient OR a
 * family member. In-person is a SIMPLIFIED path — the provider is pinned to the
 * logged-in doctor and their own clinic address is used, so none of bridge's
 * Google-Places / state-city / radius location search is needed; the server
 * still reads the vendor + providerAddressId fields, which we echo from the
 * slots response.
 */
class CreateAppointmentService {
  constructor() {
    this.apiService = new APIService();
  }

  /** { patientId, accountCodes: [] } */
  getAccountsForPid(patientId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAccountsForPid.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The patient's plans. Returns a BARE ARRAY.
   *
   * Every plan comes back with status:1 — that is the plan template's status,
   * not the patient's enrolment, and there are no start/end dates to filter on.
   * So this response CANNOT tell you what is bookable; `bookConsultation`
   * narrows it, but only getServiceProviderForPatient() knows for sure. Treat
   * its "No active plan exist for patient" 400 as the normal negative answer.
   */
  getPlansForPid(patientId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getPlansForPid.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Plan-scoped services -> { services, recommendedServices, otherServices, … }.
   * serviceType 1 = consultation.
   *
   * serviceModeDetails[].discountedPrice here is an INDICATIVE LABEL ONLY. It is
   * never the amount charged — the price comes from the provider object.
   */
  getServicesForPatient(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getServicesForPatient;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * The provider row — and the ONLY authority for `price`. Also carries
   * `addresses[]` (in-person clinics: { providerAddressId, housing, city,
   * landmark, googleMapLink }) and the vendor fields.
   *
   * serviceProviderIds is [] — EVERY provider offering the plan/service/mode
   * (widened 2026-09-03; the old self-only rule is gone, the operator's own
   * row is only pre-selected). Rows with id 0 are excluded-provider
   * placeholders (price 0) and must be filtered out — one reaching the picker
   * would book a paid consultation as free.
   *
   * pageNum/pageSize must BOTH be 0 or both non-zero, else 400.
   */
  getServiceProviderForPatient(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getServiceProviderForPatient;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Slots, plus the four vendor fields the create payload must echo back.
   *
   * For Tele/Video these come back empty ("" / 0) and that is CORRECT — the
   * server only reads them for in-person. Echo them verbatim anyway.
   *
   * serviceProviderId is the chosen provider's id — or 0 when "choose
   * automatically" is ticked (bridge's auto-select, 2026-09-10): the server then
   * picks the provider for that date and names it in the response's
   * `serviceProviderId`, which is what the booking is then made with.
   *
   * Today's list is shorter for doctor_web than for bridge_web (13 vs 96 when
   * measured) — bridge bypasses the same-day filter and so lists slots already
   * in the past. The short list is the correct, bookable one.
   */
  /**
   * The NAMES of a set of accounts — POST /v1/account/filter with
   * accountCodeMatch (exact, case-insensitive), unpaged, without the per-account
   * manager lookup. Answers { accounts: [{ accountCode, accountName, … }] }.
   * NEVER sent with an empty list: [] applies no filter at all and returns the
   * tenant's whole account catalogue.
   */
  getAccountsByCodes(codes, onResponse, onError) {
    if (!codes || !codes.length) return undefined;
    const url = APIConfig.myPlan.accountDashboard.getAccountsByFilter;

    return this.apiService.post(
      url,
      { pageNum: 0, pageSize: 0, accountCodeMatch: codes, skipManagerName: true },
      onResponse,
      onError
    );
  }

  getAvailableSlots(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAvailableSlots;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /** Wallet + sublimit for a paid booking. Read-only, so post() is fine. */
  checkCurrentWalletDetails(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.checkCurrentWalletDetails;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Create. postAgain, NEVER post.
   *
   * post()'s error path re-fires the request via postAgain (newAPIService.js
   * :340-349 + :378) on ANY non-2xx — not just 401. A 500 thrown after the row
   * is inserted would therefore book the patient twice. postAgain is a single
   * attempt. The cost is that it does not refresh an expired token, so a 401
   * surfaces as an error toast instead of auto-recovering; that is the right
   * trade for a create. Bridge makes the same call.
   *
   * Responds 201; postAgain maps 201 -> onResponse.
   */
  createAppointment(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.createAppointment;

    return this.apiService.postAgain(url, body, onResponse, onError);
  }

  /**
   * Retry a payment link for an appointment that was created without one.
   * Flat body: { appointmentId, useWallet, paymentLink } — no paymentDetails.
   *
   * postAgain for the same reason as createAppointment: this generates a link
   * and sends it to the patient, so post()'s re-fire-on-any-error would text
   * them twice.
   */
  generatePaymentLink(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.generatePaymentLink;

    return this.apiService.postAgain(url, body, onResponse, onError);
  }

  /**
   * A patient's family members for the "Booking for" picker. Responds
   * { families: [{ memberPatientId, firstName, lastName, gender, dateOfBirth
   * (YYYY-MM-DD string), relation }] }. The patient themself is NOT in this
   * list — the caller prepends them as relation 0 (Self).
   */
  getFamilyMembers(patientId, onResponse, onError) {
    const url = APIConfig.familyPlan.members.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Relation code→label master ([{ code, value, gender }]) for member labels. */
  getRelations(onResponse, onError) {
    return this.apiService.get(APIConfig.familyPlan.relations, onResponse, onError);
  }

  /**
   * Backfill a booking-for person's missing DOB/email before booking (bridge's
   * updateProfileInfo). Body: { patientId, firstName, lastName, gender,
   * dateOfBirth:"YYYY-MM-DD", phoneNo:String, email, profileImage, countryCode }.
   * A plain PUT — it's a targeted single-record update, safe to retry.
   */
  updateProfileInfo(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.updateProfileInfo;

    return this.apiService.put(url, body, onResponse, onError);
  }
}

export default CreateAppointmentService;
